import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import Table from "../../../core/common/dataTable/index"
import { fontsApi, buildUploadForm, type CertFont } from "../../../service/certificate"
import { useCustomFontFaces } from "../common/useCustomFontFaces"

const MAX_SIZE = 5 * 1024 * 1024
const ACCEPTED = ".woff2,.woff,.ttf,.otf"
const PREVIEW_TEXT = "The quick brown fox jumps over the lazy dog 0123"

// Custom typeface management. Mirrors the RMS Fonts page: a live preview
// column rendered in the actual uploaded face (via injected @font-face),
// and an upload form that only needs a file + display name — the CSS
// font-family handle is auto-derived server-side.
const CertFonts = () => {
  const [items, setItems] = useState<CertFont[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [includeInactive, setIncludeInactive] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [editing, setEditing] = useState<CertFont | "new" | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  // Inject @font-face for the active fonts so the preview column renders
  // in the uploaded face instead of the system fallback.
  useCustomFontFaces(items)

  const fetchItems = async () => {
    setLoading(true)
    try {
      const res = await fontsApi.list(includeInactive ? { includeInactive: 1 } : undefined)
      if (res.success) setItems(res.data)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load fonts")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive])

  useEffect(() => { setPage(1) }, [items.length])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(f => [f.name, f.format].filter(Boolean).some(s => String(s).toLowerCase().includes(q)))
  }, [items, search])

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await fontsApi.remove(deleteId)
      toast.success("Deleted")
      setDeleteId(null)
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed")
    }
  }

  const handleRestore = async (id: string) => {
    try {
      await fontsApi.restore(id)
      toast.success("Restored")
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Restore failed")
    }
  }

  const columns = [
    { title: "Name", dataIndex: "name" },
    {
      title: "Preview",
      dataIndex: "family",
      render: (family: string) => (
        <span style={{ fontFamily: `'${family}'`, fontSize: 18 }}>{PREVIEW_TEXT}</span>
      ),
    },
    {
      title: "Format",
      dataIndex: "format",
      render: (v: string) => (
        <span className="badge bg-light text-dark text-uppercase">{v}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (v: boolean) =>
        v === false ? (
          <span className="badge bg-secondary-transparent text-secondary">Inactive</span>
        ) : (
          <span className="badge bg-success-transparent text-success">Active</span>
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: CertFont) =>
        record.isActive === false ? (
          <button className="btn btn-sm btn-outline-success" onClick={() => handleRestore(record._id)}>
            <i className="ti ti-restore me-1" />Restore
          </button>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <Link to="#" className="btn btn-sm btn-outline-primary" onClick={() => setEditing(record)}>
              <i className="ti ti-edit" />
            </Link>
            <Link to="#" className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(record._id)}>
              <i className="ti ti-trash" />
            </Link>
          </div>
        ),
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Fonts</h3>
            <p className="text-muted mb-0">
              Upload custom typefaces to use in certificate designs. WOFF2 renders fastest in PDFs but TTF / OTF / WOFF all work.
            </p>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <input
              className="form-control"
              style={{ maxWidth: 220 }}
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="form-check form-switch mb-0 me-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="cert_fonts_show_inactive"
                checked={includeInactive}
                onChange={e => setIncludeInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="cert_fonts_show_inactive">Show inactive</label>
            </div>
            <button className="btn btn-primary" onClick={() => setEditing("new")}>
              <i className="ti ti-square-rounded-plus me-2" />Upload font
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <Table
              dataSource={visible}
              columns={columns}
              loading={loading}
              pagination={{
                currentPage: page,
                limit: pageSize,
                total: visible.length,
                onChange: (p: number, ps: number) => { setPage(p); setPageSize(ps) },
              }}
            />
          </div>
        </div>
      </div>

      {editing && (
        <FontModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchItems() }}
        />
      )}

      {/* Delete confirmation */}
      {deleteId && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center p-4">
                  <span className="text-danger" style={{ fontSize: 40 }}>
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4 className="mt-2">Delete Font</h4>
                  <p className="text-muted">
                    Templates already using it keep working — the renderer still serves the file. Continue?
                  </p>
                  <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-light" onClick={() => setDeleteId(null)}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleDelete}>Yes, Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  )
}

// Upload / edit modal. New uploads need a file + display name; the family
// handle is generated server-side. Editing only touches name + active
// (replace the file by deleting and re-uploading, matching RMS).
const FontModal = ({
  editing,
  onClose,
  onSaved,
}: {
  editing: CertFont | "new"
  onClose: () => void
  onSaved: () => void
}) => {
  const isEdit = editing !== "new"
  const [name, setName] = useState(isEdit ? editing.name : "")
  const [active, setActive] = useState(isEdit ? editing.isActive !== false : true)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) { setFile(null); return }
    if (f.size > MAX_SIZE) {
      setError(`File too large: ${(f.size / 1024 / 1024).toFixed(1)} MB exceeds 5 MB`)
      setFile(null)
      e.target.value = ""
      return
    }
    if (!/\.(woff2|woff|ttf|otf)$/i.test(f.name)) {
      setError("Only WOFF2 / WOFF / TTF / OTF allowed")
      setFile(null)
      e.target.value = ""
      return
    }
    setError(null)
    setFile(f)
    // Pre-fill the display name from the filename if still blank.
    if (!name) setName(f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " "))
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { setError("Display name is required"); return }
    if (!isEdit && !file) { setError("Pick a font file first"); return }
    setSaving(true)
    setError(null)
    try {
      if (isEdit) {
        await fontsApi.update(editing._id, { name: name.trim(), isActive: active })
        toast.success("Updated")
      } else {
        const fd = buildUploadForm({ name: name.trim() }, file, "file")
        await fontsApi.create(fd)
        toast.success("Uploaded")
      }
      onSaved()
    } catch (err: any) {
      setError(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">{isEdit ? "Edit font" : "Upload font"}</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                {isEdit ? (
                  <div className="mb-3">
                    <label className="form-label">File</label>
                    <div className="rounded border bg-light px-3 py-2 text-muted small">
                      {editing.fileKey.split("/").pop()}{" "}
                      <span className="text-uppercase">({editing.format})</span>
                    </div>
                    <small className="text-muted">To replace the file, delete this font and upload a new one.</small>
                  </div>
                ) : (
                  <div className="mb-3">
                    <label className="form-label">Font file</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="form-control"
                      accept={ACCEPTED}
                      onChange={onPickFile}
                    />
                    <small className="text-muted">
                      WOFF2 / WOFF / TTF / OTF, up to 5 MB.
                      {file ? ` Selected: ${file.name} (${(file.size / 1024).toFixed(0)} KB)` : ""}
                    </small>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Display name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={name}
                    onChange={e => setName(e.target.value)}
                  />
                  <small className="text-muted">Shown in the Designer's font dropdown (e.g. 'Playfair Display Bold').</small>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="cert_font_active"
                    checked={active}
                    onChange={e => setActive(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="cert_font_active">Active</label>
                </div>

                {error && (
                  <div className="alert alert-danger mt-3 mb-0 py-2 px-3 small">{error}</div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving…" : isEdit ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

export default CertFonts
